import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Pencil } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApiMutation } from "@/hooks/useApiMutation";
import { showError } from "@/lib/toastHelpers";


export default function AddStarterEquipment() {
  const [location, setLocation] = useLocation();

  const [editId, setEditId] = useState<string | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    categoryId: "",
    purchaseAmount: "",
    notes: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('edit');
    setEditId(id);
  }, [location]);

  const isEditMode = !!editId;

  const { data: starterEquipment, isLoading: equipmentLoading } = useQuery({
    queryKey: ["/api/starter-pack/equipment"],
    queryFn: async () => {
      return await apiRequest("GET", "/api/starter-pack/equipment");
    },
  });

  const { data: categories = [], refetch: refetchCategories } = useQuery({
    queryKey: ["/api/starter-pack/categories/equipment"],
    queryFn: async () => {
      return await apiRequest("GET", "/api/starter-pack/categories/equipment");
    },
  });

  const editingEquipment = isEditMode
    ? (starterEquipment as any[])?.find((e: any) => e._id === editId)
    : undefined;

  useEffect(() => {
    if (editingEquipment) {
      setFormData({
        name: editingEquipment.name || "",
        categoryId: editingEquipment.categoryId || "",
        purchaseAmount: editingEquipment.purchaseAmount || "",
        notes: editingEquipment.notes || "",
      });
    }
  }, [editingEquipment, categories]);

  const createCategoryMutation = useApiMutation({
    endpoint: "/api/starter-pack/categories/equipment",
    method: "POST",
    successMessage: "Category created successfully",
    onMutate: async (vars: { name: string }) => {
      const isDuplicate = (categories as any[]).some((cat: any) => cat.name.toLowerCase() === vars.name.trim().toLowerCase());
      if (isDuplicate) throw new Error("A category with this name already exists");
    },
    onSuccess: (newCategory: any) => {
      refetchCategories();
      setFormData({ ...formData, categoryId: newCategory._id });
      setNewCategoryName("");
      setShowAddCategory(false);
    },
  });

  const updateCategoryMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/starter-pack/categories/equipment/${vars.id}`,
    method: "PATCH",
    successMessage: "Category updated successfully",
    onMutate: async (vars: { id: string; name: string }) => {
      const isDuplicate = (categories as any[]).some((cat: any) => cat._id !== vars.id && cat.name.toLowerCase() === vars.name.trim().toLowerCase());
      if (isDuplicate) throw new Error("A category with this name already exists");
    },
    onSuccess: () => {
      refetchCategories();
      setShowEditCategory(false);
      setEditingCategory(null);
    },
  });

  const createMutation = useApiMutation({
    endpoint: "/api/starter-pack/equipment",
    method: "POST",
    invalidate: [["/api/starter-pack/equipment"]],
    successMessage: "Equipment created successfully",
    onSuccess: () => {
      setLocation("/starter-pack");
    },
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/starter-pack/equipment/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/starter-pack/equipment"]],
    successMessage: "Equipment updated successfully",
    onSuccess: () => {
      setLocation("/starter-pack");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError("Name is required");
      return;
    }

    const submitData = {
      ...formData,
      categoryId: formData.categoryId || null,
    };

    if (isEditMode && editId) {
      updateMutation.mutate({ id: editId, ...submitData } as any);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEditCategory = (categoryId: string) => {
    const category = (categories as any[]).find((c: any) => c._id === categoryId);
    if (category) {
      setEditingCategory(category);
      setShowEditCategory(true);
    }
  };

  if (equipmentLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isEditMode && !editingEquipment) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/starter-pack")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
            {isEditMode ? 'Edit Starter Equipment' : 'Add Starter Equipment'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update starter equipment details' : 'Create a new starter equipment template'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Equipment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Equipment name"
                required
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) => {
                      if (value === "__add_new__") {
                        setShowAddCategory(true);
                      } else {
                        setFormData({ ...formData, categoryId: value });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(categories as any[]).map((cat: any) => (
                        <SelectItem key={cat._id} value={cat._id}>{cat.name}</SelectItem>
                      ))}
                      <SelectItem value="__add_new__">+ Add New Category</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.categoryId && formData.categoryId !== "__add_new__" && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => handleEditCategory(formData.categoryId)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="purchaseAmount">Purchase Amount</Label>
              <Input
                id="purchaseAmount"
                type="number"
                step="0.01"
                value={formData.purchaseAmount}
                onChange={(e) => setFormData({ ...formData, purchaseAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLocation("/starter-pack")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-category">Category Name</Label>
              <Input
                id="new-category"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (newCategoryName.trim()) {
                  createCategoryMutation.mutate({ name: newCategoryName.trim() });
                }
              }}
              disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditCategory} onOpenChange={setShowEditCategory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-category">Category Name</Label>
              <Input
                id="edit-category"
                value={editingCategory?.name || ""}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                placeholder="Enter category name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCategory(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (editingCategory?.name.trim()) {
                  updateCategoryMutation.mutate({ id: editingCategory._id, name: editingCategory.name.trim() });
                }
              }}
              disabled={!editingCategory?.name.trim() || updateCategoryMutation.isPending}
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
